import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Theme, Tooltip, useMediaQuery } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import { RestorationType, SampleType } from 'models/order';

type RestorationTypesProps = {
    sampleType: SampleType;
    /** `Order.restorationTypes` — the distinct restoration types on the order (DD-2506). */
    types: RestorationType[];
    showIcon?: boolean;
};

/** Table-cell styling that truncates each line with an ellipsis; the full name shows on hover. */
export const RESTORATION_TYPES_CELL_SX = {
    '& .MuiBox-root': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
};

/** The list-column summary of what an order contains: the sample icon and one line per restoration type. */
const RestorationTypes: FC<RestorationTypesProps> = ({ sampleType, types, showIcon }: RestorationTypesProps) => {
    const { t: translate } = useTranslation(['orders', 'restoration-types']);
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

    return (
        <Box display="flex" alignItems="center">
            {isLargeScreen && showIcon !== false ? (
                <Box height="20px" mr={1}>
                    {sampleType === SampleType.DIGITAL ? (
                        <Tooltip title={<span>{translate('orders:DIGITAL')}</span>}>
                            <AttachFileIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                        </Tooltip>
                    ) : undefined}
                    {sampleType === SampleType.PHYSICAL ? (
                        <Tooltip title={<span>{translate('orders:PHYSICAL')}</span>}>
                            <Inventory2OutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                        </Tooltip>
                    ) : undefined}
                    {sampleType === SampleType.NO_SAMPLE ? (
                        <Tooltip title={<span>{translate('orders:NO_SAMPLE')}</span>}>
                            <CircleOutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                        </Tooltip>
                    ) : undefined}
                </Box>
            ) : undefined}
            <Box>
                {types.map((type) => {
                    const typeLabel = translate(`restoration-types:${type}`);
                    return (
                        <Box key={type} title={typeLabel}>
                            {typeLabel}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default RestorationTypes;
